import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { environment } from '../../../enviroment/enviroment';

@Component({
  selector: 'app-forms',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
  ],
  providers: [
    AuthService,
  ],
  templateUrl: './forms.component.html',
  styleUrls: ['./forms.component.css']
})
export class FormsComponent {
  form!: FormGroup;
  submitted = false;

  // FontAwesome icons
  faEye = faEye;
  faEyeSlash = faEyeSlash;

  // UI state to toggle visibility
  showPassword = false;
  showConfirmPassword = false;

  // Message state for inline HTML feedback (instead of alert)
  message: string | null = null;
  messageType: 'success' | 'error' | 'info' = 'info';
  
  // Loading state
  isLoading = false;

  // 🆕 Storage local para documentos registrados (se llena después de cada registro exitoso)
  private registeredDocuments: Set<string> = new Set();

  togglePasswordVisibility(field: 'password' | 'confirm') {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly fb: FormBuilder
  ) { }

  private static readonly NAME_REGEX = /^[A-Za-zÁÉÍÓÚÑáéíóúñ\s'-]{2,}$/;
  private static readonly PASS_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

  ngOnInit(): void {
    // 🆕 Cargar documentos registrados en sesiones anteriores
    this.loadRegisteredDocuments();

    this.form = this.fb.group(
      {
        userGroup: ['user', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        given_name: ['', [Validators.required, Validators.pattern(FormsComponent.NAME_REGEX)]],
        family_name: ['', [Validators.required, Validators.pattern(FormsComponent.NAME_REGEX)]],
        document_type: ['CC', Validators.required],
        document_number: ['', [Validators.required, Validators.pattern(/^\d{6,20}$/)]],
        rnt: [''],
        password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(FormsComponent.PASS_REGEX)]],
        confirmPass: ['', [Validators.required]],
        termsAccepted: [false, Validators.requiredTrue]
      },
      { validators: [this.passwordsMatchValidator()] }
    );

    // Validación condicional para RNT si userGroup === 'admin-hotel'
    this.form.get('userGroup')?.valueChanges.subscribe((value) => {
      const rntCtrl = this.form.get('rnt');
      if (!rntCtrl) return;
      if (value === 'admin-hotel') {
        rntCtrl.setValidators([Validators.required, Validators.pattern(/^\d{4,10}$/)]);
      } else {
        rntCtrl.clearValidators();
        rntCtrl.setValue('');
      }
      rntCtrl.updateValueAndValidity();
    });

    // Normalizar entradas numéricas: eliminar todo lo que no sea dígito
    const docCtrl = this.form.get('document_number');
    docCtrl?.valueChanges.subscribe((val: string) => {
      if (typeof val !== 'string') return;
      const cleaned = val.replace(/\D+/g, '');
      if (cleaned !== val) {
        docCtrl.setValue(cleaned, { emitEvent: false });
      }
    });

    const rntCtrl2 = this.form.get('rnt');
    rntCtrl2?.valueChanges.subscribe((val: string) => {
      if (typeof val !== 'string') return;
      const cleaned = val.replace(/\D+/g, '');
      if (cleaned !== val) {
        rntCtrl2.setValue(cleaned, { emitEvent: false });
      }
    });
  }

  private passwordsMatchValidator() {
    return (group: AbstractControl): ValidationErrors | null => {
      const pass = group.get('password')?.value;
      const confirm = group.get('confirmPass')?.value;
      if (!pass || !confirm) return null;
      if (pass !== confirm) {
        group.get('confirmPass')?.setErrors({ ...(group.get('confirmPass')?.errors || {}), mismatch: true });
        return { passwordMismatch: true };
      } else {
        const errors = group.get('confirmPass')?.errors;
        if (errors && 'mismatch' in errors) {
          delete errors['mismatch'];
          if (Object.keys(errors).length === 0) group.get('confirmPass')?.setErrors(null);
          else group.get('confirmPass')?.setErrors(errors);
        }
        return null;
      }
    };
  }

  get c() { return this.form.controls; }

  shouldShowError(controlName: string): boolean {
    const ctrl = this.form.get(controlName);
    return !!ctrl && ctrl.invalid && (ctrl.touched || this.submitted);
  }

  getError(controlName: string): string | null {
    const ctrl = this.form.get(controlName);
    if (!ctrl || !ctrl.errors) return null;
    const e = ctrl.errors;
    if (e['required']) return 'Campo obligatorio.';
    if (e['email']) return 'Formato de correo inválido.';
    if (e['duplicate']) {
      if (controlName === 'email') return 'Ya existe una cuenta con este correo.';
      if (controlName === 'document_number') return 'Ya existe un usuario con este tipo y número de documento.';
      return 'Valor ya registrado.';
    }
    if (e['minlength']) return `Mínimo ${e['minlength'].requiredLength} caracteres.`;
    if (e['pattern']) {
      if (controlName === 'password') return 'Debe tener mayúscula, minúscula, número y carácter especial.';
      if (controlName === 'document_number') return 'Solo dígitos (6 a 20).';
      if (controlName === 'rnt') return 'Solo dígitos (4 a 10).';
      return 'Formato inválido.';
    }
    if (e['mismatch']) return 'Las contraseñas no coinciden.';
    if (controlName === 'confirmPass' && this.form.hasError('passwordMismatch')) return 'Las contraseñas no coinciden.';
    return 'Valor inválido.';
  }

  async register() {
    this.submitted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.message = 'Por favor corrige los errores del formulario.';
      this.messageType = 'error';
      return;
    }
    
    // 🆕 Pre-chequeo: validar documento duplicado ANTES de enviar (SOLO frontend)
    const docCtrlPre = this.form.get('document_number');
    const emailCtrlPre = this.form.get('email');
    const docTypeCtrlPre = this.form.get('document_type');
    const docValue = String(docCtrlPre?.value || '').trim();
    
    // Validar localmente
    const checkResult = this.checkDocumentAvailabilityLocal(docValue);
    if (!checkResult.available) {
      docCtrlPre?.setErrors({ ...(docCtrlPre?.errors || {}), duplicate: true });
      docCtrlPre?.markAsTouched();
      docTypeCtrlPre?.markAsTouched();
      this.message = checkResult.message || 'Ya existe un usuario con este tipo y número de documento.';
      this.messageType = 'error';
      return;
    }
    
    // Set loading state
    this.isLoading = true;
    
    // Clear previous messages
    this.message = null;
    this.messageType = 'info';
    try {
      const {
        email,
        password,
        given_name,
        family_name,
        document_number,
        userGroup
      } = this.form.value;

      const response = await this.authService.registerUser(
        email,
        password,
        given_name,
        family_name,
        String(document_number ?? ''),
        userGroup
      );

      // 🆕 Agregar documento a la lista de registrados
      this.addRegisteredDocument(String(document_number ?? ''));

      // Si es un usuario existente y solo se agregó a un grupo, mostrar mensaje y no redirigir
      if (response && (response as any).isExistingUser && (response as any).requiresConfirmation === false) {
        this.router.navigate(['/loggin']);
        return;
      }
      // Si requiere confirmación, redirigir como antes
      this.router.navigate(['/confirm-registration'], { 
        queryParams: { email }
      });
    } catch (error: any) {
      this.isLoading = false;
      console.error('Error en el registro:', error);
      const emailCtrl = this.form.get('email');
      const docCtrl = this.form.get('document_number');
      const docTypeCtrl = this.form.get('document_type');
      const status = error?.status;
      const code = (error?.code || '').toString();
      const msg = (error?.message || '').toString().toLowerCase();
      const detailsStr = JSON.stringify(error?.details || {}).toLowerCase();
      const newEmailExistingDocument = error?.newEmailExistingDocument ?? false;

      // ✅ Nuevo: Validar si el email es nuevo pero el documento ya existe
      if (newEmailExistingDocument || (status === 409 && detailsStr.includes('documento') && detailsStr.includes('existe'))) {
        docCtrl?.setErrors({ ...(docCtrl?.errors || {}), duplicate: true });
        docCtrl?.markAsTouched();
        docTypeCtrl?.markAsTouched();
        this.message = 'Ya existe un usuario registrado con este tipo y número de documento. Por favor usa un documento diferente o inicia sesión si ya tienes una cuenta.';
        this.messageType = 'error';
        return;
      }

      const isUserExists = status === 409 ||
        code.includes('UserExists') ||
        code.includes('USER_ALREADY_EXISTS') ||
        code.includes('EMAIL_ALREADY_REGISTERED') ||
        msg.includes('ya existe') ||
        msg.includes('already exists');

      const isDocDuplicate = status === 409 && (
        code.includes('DOCUMENT') ||
        code.includes('DOC_DUPLICATE') ||
        msg.includes('documento') ||
        msg.includes('document') ||
        detailsStr.includes('document') ||
        detailsStr.includes('documento')
      );

      if (isDocDuplicate && docCtrl) {
        docCtrl.setErrors({ ...(docCtrl.errors || {}), duplicate: true });
        docCtrl.markAsTouched();
        // Opcional: marcar también el tipo para guiar al usuario
        docTypeCtrl?.markAsTouched();
        this.message = 'Ya existe un usuario con este tipo y número de documento.';
        this.messageType = 'error';
        return;
      }

      if (isUserExists && emailCtrl) {
        emailCtrl.setErrors({ ...(emailCtrl.errors || {}), duplicate: true });
        emailCtrl.markAsTouched();
        this.message = 'El usuario ya existe. Si no has confirmado tu cuenta, revisa tu correo o solicita reenviar el código.';
        this.messageType = 'error';
        return;
      }

      // Fallback genérico
      this.message = 'Algo salió mal, lo siento. Por favor, inténtalo de nuevo más tarde.';
      this.messageType = 'error';
    } finally {
      this.isLoading = false;
    }
  }

  // 🆕 Método para validar documento SOLO en el front
  private checkDocumentAvailabilityLocal(
    documentNumber: string
  ): { available: boolean; message?: string } {
    // Verificar si el documento ya fue registrado en esta sesión
    if (this.registeredDocuments.has(documentNumber)) {
      return {
        available: false,
        message: 'El documento ya está registrado.'
      };
    }
    return { available: true };
  }

  // 🆕 Agregar documento a la lista de registrados (llamar después de registro exitoso)
  private addRegisteredDocument(documentNumber: string): void {
    this.registeredDocuments.add(documentNumber);
    // Opcional: guardar en localStorage para persistencia entre sesiones
    try {
      const stored = localStorage.getItem('registeredDocuments') || '[]';
      const docs = JSON.parse(stored);
      if (!docs.includes(documentNumber)) {
        docs.push(documentNumber);
        localStorage.setItem('registeredDocuments', JSON.stringify(docs));
      }
    } catch (e) {
      console.warn('No se pudo guardar documento en localStorage:', e);
    }
  }

  // 🆕 Cargar documentos registrados al inicializar el componente
  private loadRegisteredDocuments(): void {
    try {
      const stored = localStorage.getItem('registeredDocuments') || '[]';
      const docs = JSON.parse(stored);
      this.registeredDocuments = new Set(docs);
    } catch (e) {
      console.warn('No se pudo cargar documentos de localStorage:', e);
      this.registeredDocuments = new Set();
    }
  }
}

